<template>
  <div class="analytics-container">
    <h2 class="page-title">渠道规则管理</h2>
    <div class="table-section">
      <div style="display: flex; justify-content: space-between; margin-bottom: 12px;">
        <span style="font-weight: 600;">渠道规则列表</span>
        <el-button type="primary" @click="openRuleDialog()">新增规则</el-button>
      </div>
      <el-table :data="channelRules" border stripe style="width: 100%;">
        <el-table-column prop="name" label="渠道名称" width="180" />
        <el-table-column prop="matchMode" label="匹配方式" width="120">
          <template #default="scope">
            {{ scope.row.matchMode === 'all' ? '全部满足' : '任一满足' }}
          </template>
        </el-table-column>
        <el-table-column label="规则表达式" min-width="360">
          <template #default="scope">
            <div v-for="(r, idx) in scope.row.rules || []" :key="idx">
              <span>{{ idx + 1 }}. {{ r.description || formatRule(r) }}</span>
            </div>
          </template>
        </el-table-column>
        <el-table-column prop="enabled" label="启用" width="80">
          <template #default="scope">
            <el-tag :type="scope.row.enabled ? 'success' : 'info'">{{ scope.row.enabled ? '是' : '否' }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column label="操作" width="230" fixed="right">
          <template #default="scope">
            <el-button size="small" @click="openRuleDialog(scope.row)">编辑</el-button>
            <el-button size="small" type="primary" plain @click="handleDuplicateRule(scope.row)">复制</el-button>
            <el-button size="small" type="danger" @click="handleDeleteRule(scope.row.id)">删除</el-button>
          </template>
        </el-table-column>
      </el-table>
    </div>

    <el-dialog
      v-model="ruleDialogVisible"
      :title="ruleForm.id ? '编辑渠道规则' : '新增渠道规则'"
      width="760px"
      :close-on-click-modal="false"
    >
      <el-form :model="ruleForm" label-width="120px">
        <el-form-item label="渠道名称" required>
          <el-input v-model="ruleForm.name" placeholder="例如：rabigame" />
        </el-form-item>
        <el-form-item label="匹配方式" required>
          <el-radio-group v-model="ruleForm.matchMode">
            <el-radio label="all">全部规则满足</el-radio>
            <el-radio label="any">任一规则满足</el-radio>
          </el-radio-group>
        </el-form-item>
        <el-form-item label="规则列表" required>
          <div style="width: 100%;">
            <div v-for="(item, idx) in ruleForm.rules" :key="idx" style="display: flex; gap: 8px; margin-bottom: 8px;">
              <el-select v-model="item.type" style="width: 160px;">
                <el-option label="URL包含" value="url_contains" />
                <el-option label="URL不包含" value="url_not_contains" />
                <el-option label="URL参数包含" value="url_param_contains" />
                <el-option label="URL参数不包含" value="url_param_not_contains" />
              </el-select>
              <el-input
                v-if="item.type === 'url_param_contains' || item.type === 'url_param_not_contains'"
                v-model="item.param"
                placeholder="参数名，如 gamechannel"
                style="width: 180px;"
              />
              <el-input v-model="item.value" placeholder="匹配值" style="flex: 1;" />
              <el-button type="danger" @click="removeRuleCondition(idx)">删除</el-button>
            </div>
            <el-button @click="addRuleCondition">+ 添加规则</el-button>
            <div style="margin-top: 10px; color: var(--el-text-color-secondary);">
              公式预览：{{ ruleFormulaPreview }}
            </div>
          </div>
        </el-form-item>
        <el-form-item label="启用">
          <el-switch v-model="ruleForm.enabled" />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="ruleDialogVisible = false">取消</el-button>
        <el-button type="primary" :loading="ruleSaving" @click="saveRule">保存</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import { AdSenseChannelRuleApi } from '../../api/adsenseChannelRuleApi'

const channelRules = ref([])
const ruleDialogVisible = ref(false)
const ruleSaving = ref(false)
const ruleForm = ref({
  id: '',
  name: '',
  matchMode: 'all',
  enabled: true,
  order: 9999,
  rules: [{ type: 'url_contains', value: '' }]
})

function formatRule(rule) {
  if (!rule) return ''
  if (rule.type === 'url_contains') return `URL包含[${rule.value || ''}]`
  if (rule.type === 'url_not_contains') return `URL不包含[${rule.value || ''}]`
  if (rule.type === 'url_param_contains') return `URL参数[${rule.param || ''}]包含[${rule.value || ''}]`
  if (rule.type === 'url_param_not_contains') return `URL参数[${rule.param || ''}]不包含[${rule.value || ''}]`
  return '未知规则'
}

const ruleFormulaPreview = computed(() => {
  const modeText = ruleForm.value.matchMode === 'all' ? ' AND ' : ' OR '
  const parts = (ruleForm.value.rules || []).map((r) => formatRule(r))
  return parts.length ? parts.join(modeText) : '（请添加规则）'
})

const loadChannelRules = async () => {
  try {
    channelRules.value = await AdSenseChannelRuleApi.getList()
  } catch (error) {
    console.error('Failed to load channel rules:', error)
    ElMessage.error('加载渠道规则失败：' + (error.response?.data?.status?.message || error.message))
  }
}

const addRuleCondition = () => {
  ruleForm.value.rules.push({ type: 'url_contains', value: '' })
}

const removeRuleCondition = (idx) => {
  ruleForm.value.rules.splice(idx, 1)
}

const openRuleDialog = (row = null) => {
  if (row) {
    ruleForm.value = {
      id: row.id,
      name: row.name || '',
      matchMode: row.matchMode || 'all',
      enabled: row.enabled !== false,
      order: Number(row.order) || 9999,
      rules: JSON.parse(JSON.stringify(row.rules || [{ type: 'url_contains', value: '' }]))
    }
  } else {
    ruleForm.value = {
      id: '',
      name: '',
      matchMode: 'all',
      enabled: true,
      order: 9999,
      rules: [{ type: 'url_contains', value: '' }]
    }
  }
  ruleDialogVisible.value = true
}

const saveRule = async () => {
  if (!ruleForm.value.name || !ruleForm.value.name.trim()) {
    ElMessage.warning('请输入渠道名称')
    return
  }
  if (!ruleForm.value.rules.length) {
    ElMessage.warning('请至少添加一条规则')
    return
  }
  if (ruleForm.value.rules.some((r) => !r.value || !r.value.trim())) {
    ElMessage.warning('规则匹配值不能为空')
    return
  }
  if (ruleForm.value.rules.some((r) => (r.type === 'url_param_contains' || r.type === 'url_param_not_contains') && (!r.param || !r.param.trim()))) {
    ElMessage.warning('URL参数包含规则必须填写参数名')
    return
  }

  ruleSaving.value = true
  try {
    const payload = {
      name: ruleForm.value.name.trim(),
      matchMode: ruleForm.value.matchMode,
      enabled: ruleForm.value.enabled,
      order: Number(ruleForm.value.order) || 9999,
      rules: ruleForm.value.rules.map((r) => ({
        type: r.type,
        value: (r.value || '').trim(),
        ...((r.type === 'url_param_contains' || r.type === 'url_param_not_contains') ? { param: (r.param || '').trim() } : {})
      }))
    }
    if (ruleForm.value.id) {
      await AdSenseChannelRuleApi.update(ruleForm.value.id, payload)
    } else {
      await AdSenseChannelRuleApi.create(payload)
    }
    ElMessage.success('保存成功')
    ruleDialogVisible.value = false
    await loadChannelRules()
  } catch (error) {
    console.error('Failed to save channel rule:', error)
    ElMessage.error('保存失败：' + (error.response?.data?.status?.message || error.message))
  } finally {
    ruleSaving.value = false
  }
}

const handleDeleteRule = async (id) => {
  try {
    await ElMessageBox.confirm('确定删除这条渠道规则吗？', '提示', {
      confirmButtonText: '确定',
      cancelButtonText: '取消',
      type: 'warning'
    })
    await AdSenseChannelRuleApi.delete(id)
    ElMessage.success('删除成功')
    await loadChannelRules()
  } catch (error) {
    if (!error || error === 'cancel') return
    console.error('Failed to delete channel rule:', error)
    ElMessage.error('删除失败：' + (error.response?.data?.status?.message || error.message))
  }
}

function buildDuplicateRuleName(sourceName) {
  const base = `${(sourceName || '').trim() || '未命名渠道'}_副本`
  const existingNames = new Set((channelRules.value || []).map((item) => (item.name || '').trim()))
  if (!existingNames.has(base)) return base
  let idx = 2
  while (existingNames.has(`${base}${idx}`)) {
    idx += 1
  }
  return `${base}${idx}`
}

const handleDuplicateRule = async (row) => {
  try {
    const payload = {
      name: buildDuplicateRuleName(row?.name),
      matchMode: row?.matchMode || 'all',
      enabled: row?.enabled !== false,
      order: Number(row?.order) || 9999,
      rules: (row?.rules || []).map((r) => ({
        type: r.type,
        value: (r.value || '').trim(),
        ...((r.type === 'url_param_contains' || r.type === 'url_param_not_contains') ? { param: (r.param || '').trim() } : {})
      }))
    }
    await AdSenseChannelRuleApi.create(payload)
    ElMessage.success('复制成功')
    await loadChannelRules()
  } catch (error) {
    console.error('Failed to duplicate channel rule:', error)
    ElMessage.error('复制失败：' + (error.response?.data?.status?.message || error.message))
  }
}

onMounted(() => {
  loadChannelRules()
})
</script>

<style scoped>
.analytics-container {
  padding: 20px;
}

.page-title {
  margin: 0 0 20px 0;
  font-size: 24px;
  font-weight: 600;
  color: var(--el-text-color-primary);
}

.table-section {
  background-color: var(--el-bg-color);
  border-radius: 4px;
  padding: 15px;
}
</style>
